import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const DEFAULT_HERO_SECTION_ID = "homepage-hero";
const DEFAULT_CTA_SECTION_ID = "homepage-cta";
const DEFAULT_MARKETING_BASE_PATH = "/homepage";

function normalizePath(path: string): string {
  return path.replace(/\/$/, "") || "/";
}

function getScrolledPastHeroHalf(heroSectionId: string): boolean {
  const hero = document.getElementById(heroSectionId);
  if (!hero) return false;
  const rect = hero.getBoundingClientRect();
  const scrollY = window.scrollY;
  const heroTopDoc = scrollY + rect.top;
  const threshold = heroTopDoc + rect.height * 0.5;
  return scrollY >= threshold;
}

export type HomepageStickyCtaProps = {
  /** Element id of the hero section (scroll visibility threshold). */
  heroSectionId?: string;
  /** Element id of the CTA block used as a fallback when no `onEmailContinue` is wired. */
  ctaSectionId?: string;
  /** Route pathname this sticky bar is tied to (e.g. `/homepage`, `/resources`). */
  marketingBasePath?: string;
  /**
   * Extra path prefixes (e.g. `/newsletter`) so in-page scroll works on nested routes
   * like `/newsletter/:slug` while `marketingBasePath` stays the main marketing URL for navigations from elsewhere.
   */
  activePathPrefixes?: string[];
  /** Called with the trimmed email after client-side validation passes. */
  onEmailContinue?: (email: string) => void;
};

export default function HomepageStickyCta({
  heroSectionId = DEFAULT_HERO_SECTION_ID,
  ctaSectionId = DEFAULT_CTA_SECTION_ID,
  marketingBasePath = DEFAULT_MARKETING_BASE_PATH,
  onEmailContinue,
}: HomepageStickyCtaProps) {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(false);

  useEffect(() => {
    const onScrollOrResize = () => {
      setVisible(getScrolledPastHeroHalf(heroSectionId));
    };

    onScrollOrResize();
    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [heroSectionId]);

  const handleJoin = useCallback(() => {
    const v = email.trim();
    if (!EMAIL_RE.test(v)) {
      setEmailError(true);
      return;
    }
    setEmailError(false);
    if (onEmailContinue) {
      onEmailContinue(v);
      setEmail("");
      return;
    }
    const target = document.getElementById(ctaSectionId);
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    void navigate({
      pathname: normalizePath(marketingBasePath),
      hash: `#${ctaSectionId}`,
    });
  }, [email, onEmailContinue, ctaSectionId, marketingBasePath, navigate]);

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-[100] border-t border-[#9A9DA6] bg-[#121212]/80 pb-[env(safe-area-inset-bottom,0px)] backdrop-blur-md transition-transform duration-300 ease-out motion-reduce:transition-none ${
        visible ? "translate-y-0" : "pointer-events-none translate-y-full"
      }`}
      style={{ fontFamily: "var(--font-fira)" }}
      role="region"
      aria-label="Join community"
      aria-hidden={!visible}
    >
      <div className="mx-auto flex w-[90%] flex-row items-stretch gap-2 py-3 md:w-[80%] md:items-center md:justify-between md:gap-6 md:py-4">
        <div className="hidden min-w-0 flex-1 pr-2 text-left md:block">
          <p className="font-fira-condensed text-sm font-semibold leading-snug text-white sm:text-base md:text-lg">
            Join 10M+ people staying ahead of AI
          </p>
          <p className="mt-0.5 text-xs text-white/55 sm:text-sm">
            Free · WhatsApp community · Every week
          </p>
        </div>
        <div className="flex w-full flex-row items-stretch gap-2 md:w-auto md:shrink-0 md:gap-3">
        <label className="sr-only" htmlFor="sticky-cta-email">
          Email address
        </label>
        <input
          id="sticky-cta-email"
          name="email"
          type="email"
          autoComplete="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (emailError) setEmailError(false);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleJoin();
            }
          }}
          aria-invalid={emailError}
          className={`min-h-11 min-w-0 flex-1 rounded-lg border bg-black/40 px-3 py-2.5 font-fira text-sm text-white placeholder:text-white/40 backdrop-blur-sm focus:outline-none focus:ring-1 focus:ring-white/30 md:min-h-12 md:w-[26rem] md:max-w-none md:flex-none md:px-4 md:text-[0.9375rem] ${
            emailError
              ? "border-red-400/50 focus:border-red-400/50"
              : "border-white/25 focus:border-white/40"
          }`}
        />
        <button
          type="button"
          onClick={handleJoin}
          className="min-h-11 shrink-0 whitespace-nowrap rounded-lg border border-[#FFFFFF4D] bg-white px-4 py-2.5 font-fira text-sm font-semibold text-black transition-opacity hover:opacity-90 md:min-h-12 md:px-6 md:text-[0.9375rem]"
        >
          Join Now
        </button>
        </div>
      </div>
    </div>
  );
}
